import { XMarkIcon } from '@heroicons/react/24/solid'
import { Dialog, DialogContent } from '@mui/material'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { PaymentMethods } from '../../components/payment-method/payment-card'
import { Button } from '../../components/ui/button'
import { useApp } from '../../context/usePortal'
import { ayeApi } from '../../core'
import { GeneratePayUrlResponse } from '../../core/api/http-methods/generate-pay-url'
import ClosePopupConfirmationPopup from './components/ClosePopupConfirmationPopup'
import { DebitCardEWalletPopup } from './components/DebitCardEWalletPopup'
import DuitnowInstructions from './components/DuitnowInstructions'
import { DuitnowPopup } from './components/DuitnowPopup'
import FailedPopup from './components/FailedPopup'
import Greeting from './components/Greeting'
import LoadingPopup from './components/LoadingPopup'
import RecentActivities from './components/RecentActivities'
import ReferralDialog from './components/ReferralDialog'
import RewardPointsSection from './components/RewardPointsSection'
import RewardsSection from './components/RewardsSection'
import SelectMethod from './components/SelectMethod'
import SucceedPopup from './components/SucceedPopup'

const deliveryMethods = [
  {
    id: 1,
    title: 'Duitnow',
    turnaround: 'Available once consent is approved through your bank app.',
    price: '',
  },
  {
    id: 2,
    title: 'TNG / Credit or Debit Cards',
    turnaround:
      'Activate by entering card details; RM 1.00 fee refunded after successful transaction.',
    price: 'Fee RM 1.00',
  },
]

const Home: React.FC = () => {
  const { paymentMethods: ayePaymentMethods } = useApp()
  const [paymentMethods, setPaymentMethods] =
    useState<Awaited<ReturnType<typeof ayePaymentMethods>>>()
  const [succeed, setSucceed] = useState(false)
  const [isSelecting, setIsSelecting] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isTimerStarted, setIsTimerStarted] = useState(false)
  const { user } = useApp()
  const selectedDeliveryMethod = useRef(deliveryMethods[0])
  const idNumber = useRef<string>()
  const [open, setOpen] = useState<boolean>(false)
  const [paymentMethodsModified, setPaymentMethodsModified] =
    useState<boolean>(true)
  const [recentlyAddedPaymentMethod, setRecentlyAddedPaymentMethod] =
    useState<string>('')
  const idList = [
    {
      label: 'Phone Number',
      value: '5',
    },
    {
      label: 'Passport',
      value: '3',
    },
    {
      label: 'IC',
      value: '1',
    },
  ]
  const idType = useRef<string>(idList[0]?.value)
  const location = useLocation()
  const navigate = useNavigate()
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false)
  const onClose = () => {
    if (!(isSubmitted && !isTimerStarted)) {
      if (showConfirmation) {
        setOpen(false)
        setShowConfirmation(false)
      } else {
        setShowConfirmation(true)
      }
    } else {
      handleConfirmClose()
    }
  }


  const handleConfirmClose = () => {
    //if current url is ../proal/home/23343
    if (!location.pathname.endsWith('/portal')) {
      navigate('/portal')
    }
    setOpen(false)
    setIsLoading(false)
    setShowConfirmation(false)
    setIsSubmitting(false)
    setIsSubmitted(false)
    setIsTimerStarted(false)
  }

  const setSelectedDeliveryMethod = (id: number) => {
    selectedDeliveryMethod.current = deliveryMethods[id]
  }
  useEffect(() => {
    // console.log('payment method hcnaged')
    ayePaymentMethods().then((data) => {
      setPaymentMethods(data)
      // console.log('payment method hcnaged')
    })
  }, [paymentMethodsModified])

  const setPhoneNumber = (value: string) => {
    console.log(value)
  }

  const setID = (value: string) => {
    idNumber.current = value
  }
  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true)
    setIsLoading(true)
    const response: GeneratePayUrlResponse = await ayeApi.generatePayUrl({
      body: {
        phoneNumber: `${user?.user_phone_number}`,
        countryCode: user?.country_code || '',
        userId: user?.user_id,
      },
    })
    //@ts-ignore
    if (response.success) window.open(response.data.pay_url, '_self')
  }, [])

  const goBackToSelectMethod = () => {
    setIsSelecting(true)
    setIsSubmitting(false)
  }

  const selectMethod = (id: number) => {
    setIsSelecting(false)
    setSelectedDeliveryMethod(id)
    setIsSubmitting(true)
  }

  const handleChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    idType.current = event.target.value
  }

  const idTypeError = () => {
    return idNumber.current ? idNumber.current.length < 7 : false
  }

  const phoneNumberError = () => {
    return false
  }

  const tryAgain = () => {
    setIsSubmitted(false)
    setIsSelecting(true)
  }
  const handleOpen = () => {
    setIsSelecting(true)
    setOpen(true)
  }

  useEffect(() => {
    const url = new URLSearchParams(window.location.search)
    const status = url.get('status')

    if (status === 'success') {
      setSucceed(true)
    } else if (status === 'failed') {
      setSucceed(false)
    }
  }, [])

  return (
    <>
      <div className="">
        <Greeting />
        <div className="flex flex-col gap-y-4 px-8 sm:px-6 md:flex-row md:justify-between lg:px-8">
          <h2 className="text-lg font-bold leading-6 text-gray-900 md:text-xl">
            Payment Methods Overview
          </h2>
          <Button
            onClick={() => handleOpen()}
            variant="outline"
            className={
              'max-w-[13rem] rounded-full bg-[#5CB891] px-2 text-primary-foreground hover:bg-[#5CB891]/80'
            }>
            <span className="text-sm font-semibold">Add New Payment Method</span>
          </Button>
        </div>
        <PaymentMethods />

        <RewardPointsSection />

        <RewardsSection />

        <RecentActivities />
        {open && (
          <div className="rounded-2xl p-0">
            <Dialog
              open={open}
              onClose={onClose}
              className="boder-red-300 rounded-xl border-1 p-0">
              <DialogContent dividers className="max-w-[400px] p-0">
                {!(isSubmitted && !isTimerStarted) && (
                  <button onClick={onClose} className="ms-auto block">
                    <XMarkIcon className="h-6 text-end" />
                  </button>
                )}
                {!isSubmitted && isSubmitting && !isSelecting ? (
                  selectedDeliveryMethod.current?.id === 2 ? (
                    <DebitCardEWalletPopup
                      setPhoneNumber={setPhoneNumber}
                      phoneNumberError={phoneNumberError}
                      handleSubmit={handleSubmit}
                      goBackToSelectMethod={goBackToSelectMethod}
                    />
                  ) : (
                    <DuitnowPopup
                      handleSubmit={handleSubmit}
                      idTypeError={idTypeError}
                      handleChange={handleChange}
                      setID={setID}
                      idList={idList}
                      idType={idType.current}
                      setPhoneNumber={setPhoneNumber}
                      phoneNumberError={phoneNumberError}
                      goBackToSelectMethod={goBackToSelectMethod}
                    />
                  )
                ) : (
                  // ) : isSelecting && <DuitnowInstructions/>
                  isSelecting && <SelectMethod selectMethod={selectMethod} />
                )}

                {isLoading && <LoadingPopup />}

                {/* {
                (isTimerStarted && isSubmitted) && <DuitnowInstructions/>
              } */}
                {isSubmitted &&
                  (isTimerStarted ? (
                    <DuitnowInstructions />
                  ) : succeed ? (
                    <SucceedPopup
                      onClose={onClose}
                      addedPaymentMethod={recentlyAddedPaymentMethod}
                    />
                  ) : (
                    <FailedPopup onClose={onClose} tryAgain={tryAgain} />
                  ))}
              </DialogContent>
            </Dialog>
          </div>
        )}
        <ClosePopupConfirmationPopup
          open={showConfirmation}
          onClose={() => setShowConfirmation(false)}
          onConfirm={handleConfirmClose}
        />
      </div>

      <ReferralDialog />
    </>
  )
}

export default Home
